import {
  CheckCircle2,
  Play,
  RotateCcw,
  Shield,
  XCircle,
  Plus,
  Pencil,
  Trash2,
  MessageSquare,
  Upload,
  FolderKanban,
  User,
} from 'lucide-react';
import type { ActivityItem } from 'shared/types/index.js';

export function formatActivityAction(item: ActivityItem): {
  icon: React.ReactNode;
  label: string;
  color: string;
} {
  const action = item.action;
  const entityType = item.entity_type;

  // Step actions
  if (action.includes('/start')) {
    return { icon: <Play className="w-3 h-3 text-blue-500" />, label: 'が工程を開始しました', color: 'bg-blue-100 dark:bg-blue-900/40' };
  }
  if (action.includes('/submit')) {
    return { icon: <Shield className="w-3 h-3 text-purple-500" />, label: 'が工程を提出しました', color: 'bg-purple-100 dark:bg-purple-900/40' };
  }
  if (action.includes('/approve')) {
    return { icon: <CheckCircle2 className="w-3 h-3 text-green-500" />, label: 'が承認しました', color: 'bg-green-100 dark:bg-green-900/40' };
  }
  if (action.includes('/reject')) {
    return { icon: <XCircle className="w-3 h-3 text-red-500" />, label: 'が差戻しました', color: 'bg-red-100 dark:bg-red-900/40' };
  }
  if (action.includes('/reopen')) {
    return { icon: <RotateCcw className="w-3 h-3 text-amber-500" />, label: 'が工程を再開しました', color: 'bg-amber-100 dark:bg-amber-900/40' };
  }
  if (action.includes('/actions') && action.startsWith('PUT')) {
    return { icon: <Pencil className="w-3 h-3 text-blue-500" />, label: 'がアクションを更新しました', color: 'bg-blue-100 dark:bg-blue-900/40' };
  }

  // Project actions
  if (entityType === 'projects' && action.startsWith('POST')) {
    return { icon: <Plus className="w-3 h-3 text-green-500" />, label: 'がプロジェクトを作成しました', color: 'bg-green-100 dark:bg-green-900/40' };
  }
  if (entityType === 'projects' && action.startsWith('PUT')) {
    return { icon: <Pencil className="w-3 h-3 text-blue-500" />, label: 'がプロジェクトを更新しました', color: 'bg-blue-100 dark:bg-blue-900/40' };
  }
  if (entityType === 'projects' && action.startsWith('DELETE')) {
    return { icon: <Trash2 className="w-3 h-3 text-red-500" />, label: 'がプロジェクトを削除しました', color: 'bg-red-100 dark:bg-red-900/40' };
  }

  // Steps
  if (entityType === 'steps' && action.startsWith('POST')) {
    return { icon: <Plus className="w-3 h-3 text-green-500" />, label: 'が工程を追加しました', color: 'bg-green-100 dark:bg-green-900/40' };
  }
  if (entityType === 'steps' && action.startsWith('PUT')) {
    return { icon: <Pencil className="w-3 h-3 text-blue-500" />, label: 'が工程を更新しました', color: 'bg-blue-100 dark:bg-blue-900/40' };
  }
  if (entityType === 'steps' && action.startsWith('DELETE')) {
    return { icon: <Trash2 className="w-3 h-3 text-red-500" />, label: 'が工程を削除しました', color: 'bg-red-100 dark:bg-red-900/40' };
  }

  // Files
  if (entityType === 'files') {
    return { icon: <Upload className="w-3 h-3 text-indigo-500" />, label: 'がファイルをアップロードしました', color: 'bg-indigo-100 dark:bg-indigo-900/40' };
  }

  // Users
  if (entityType === 'users') {
    return { icon: <User className="w-3 h-3 text-gray-500" />, label: 'がユーザー情報を更新しました', color: 'bg-gray-100 dark:bg-gray-700' };
  }

  // Flows
  if (entityType === 'flows' && action.startsWith('POST')) {
    return { icon: <Plus className="w-3 h-3 text-teal-500" />, label: 'がフローを作成しました', color: 'bg-teal-100 dark:bg-teal-900/40' };
  }
  if (entityType === 'flows') {
    return { icon: <Pencil className="w-3 h-3 text-teal-500" />, label: 'がフローを更新しました', color: 'bg-teal-100 dark:bg-teal-900/40' };
  }

  // Default
  return { icon: <FolderKanban className="w-3 h-3 text-gray-500" />, label: 'が操作しました', color: 'bg-gray-100 dark:bg-gray-700' };
}

/** details JSON から有用な情報を抽出 */
export function parseActivityDetails(details: string | null): {
  comment: string | null;
} {
  if (!details) return { comment: null };
  try {
    const parsed = JSON.parse(details);
    // コメント・理由フィールドを探す
    const comment = parsed.comment || parsed.reason || parsed.rejection_reason || parsed.message || null;
    return { comment: typeof comment === 'string' ? comment : null };
  } catch {
    return { comment: null };
  }
}

/** SQLite datetime (YYYY-MM-DD HH:MM:SS) を ISO形式に変換 */
export function parseActivityDate(dateStr: string): Date {
  // SQLiteの "2026-04-16 12:34:56" → "2026-04-16T12:34:56Z"
  const iso = dateStr.includes('T') ? dateStr : dateStr.replace(' ', 'T') + 'Z';
  return new Date(iso);
}

export function formatAbsoluteTime(dateStr: string): string {
  return parseActivityDate(dateStr).toLocaleString('ja-JP', { timeZone: 'Asia/Tokyo' });
}

export function formatRelativeTime(dateStr: string): string {
  const now = Date.now();
  const date = parseActivityDate(dateStr).getTime();
  const diff = Math.floor((now - date) / 1000);

  if (diff < 60) return 'たった今';
  if (diff < 3600) return `${Math.floor(diff / 60)}分前`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}時間前`;
  if (diff < 172800) return '昨日';
  if (diff < 604800) return `${Math.floor(diff / 86400)}日前`;
  return parseActivityDate(dateStr).toLocaleDateString('ja-JP');
}
